import { ENDPOINT } from "../environment";

// Base API configuration
const apiVersion = "/app/v1/";
const baseUrl = `${ENDPOINT}${apiVersion}`;

// Centralized API configuration
export const ApiConfig = {
  baseUrl,
  getFullUrl: (endpoint: string, params?: Record<string, string | number>) => {
    let resolvedEndpoint = endpoint;
    if (params) {
      Object.keys(params).forEach((key) => {
        resolvedEndpoint = resolvedEndpoint.replace(`:${key}`, String(params[key]));
      });
    }
    return `${baseUrl}${resolvedEndpoint}`;
  },
};

// Endpoints with static and dynamic options
export const endPoints = {
  getPartnerBranding: "partners/branding/view",
  priceReview: "price/review",
  // Share whole cart (flights + hotels) from the review page
  createSharedCart: "shared-cart",
  getSharedCart: "shared-cart/:id",
  sharedCartDetails: "search/shared-details",
  handoffRedeem: "handoff/redeem",
  createAskZoyaRecords: "createAskZoyaRecords",
  getRecentTravelerList: "travellers",
  getUserLocation: "https://api.radar.io/v1/geocode/ip",
  getPromptList: "getWebsitePrompts",
  getPromptTemplate: "systemprompts",
  getPlaceDetails: "itinerary/activity/details",
  accessToken: "auth/passKey",
  //App Messages
  getApplicationToastMessages: "message",
  //booking
  flightAvailability: "flight/check",
  flightDetails: "flight/flightId",
  flightSearch: "flight/search",
  hotelSearch: "hotel/search",
  airports: "flight/airports",
  hotelDetail: (hotelId: string) => `hotel/${hotelId}`,
  hotelDetailsByUnicaId: "search/hotels/details-by-unicaid",
  // Hotel-filter Property-name typeahead: POST body { city, country, query, limit }
  // → { data: { suggestions: [{ name, id }], total } }. Query is the user's typed
  // text with leading/trailing whitespace trimmed only (spaces within preserved,
  // NOT slugified). Callers debounce + abort the previous in-flight request.
  hotelNameSuggest: "search/hotels/name-suggest",
  // Hybrid Stays location search (ENABLE_HOTEL_SEARCH_GA_HYBRID): app-backend ranks
  // the autocomplete answers the browser sends, running the text searches itself.
  hotelGASuggestions: "search/hotels/ga/suggestions",

  // Baggage and fare rules
  fareRulesDetails: "search/flights/farerules",
  baggageAndFareRules: "trips/flights/farerules",
  // The partner branding a booked trip's My Trips PDF is drawn in (owner-scoped).
  tripDocumentBranding: "trips/document-branding",
  fareDetails: "search/flights/fares",
  // preferences
  preference: `users/preferences/userId`,
  updatePreference: "users/preferences",
  preferenceHistory: "users/preferences/history",
  restorePreference: "users/preferences/restore",
  categories: "config/categories",
  //user
  // userDetails: (userId: string) => `users/${userId}`,
  getUserDetails: "users/userId",
  createUser: "users",
  updateUserDetails: "users",
  deleteUser: (userId: string) => `users/${userId}`,
  //authentication
  sendResetPassword: "users/passwordlink",
  resetPassword: "users/password",
  // Signup email-verification OTP
  sendSignupOtp: "auth/signup/send-otp",
  verifySignupOtp: "auth/signup/verify-otp",
  // Generic email-ownership OTP (traveler-form lead email change)
  sendEmailOtp: "auth/email/send-otp",
  verifyEmailOtp: "auth/email/verify-otp",
  getUserDetailByToken: `users/detailByAuthToken`,
  login: "auth/login",
  logout: "auth/logout",
  template: "template/getByParam",
  //itinerary
  getItineraryDetails: (id: string) => `itineraries?id=${id}`,
  getItineraryImageURL: `itineraries/getImage`,
  unsplashTrackDownload: `itineraries/unsplashTrackDownload`,
  saveItineraryDetails: "users/itineraries",
  deleteItinerary: (id: string) => `itineraries/delete?id=${id}`,
  createItinerary: "createItenery",
  itinerarySearch: "itineraries/search",
  getItineraryBudget: (cityName: string) => `budget/${cityName}`,
  deleteHistory: (id: string) => `users/history/${id}`,

  //trip related urls
  frequentlyViewedTrips: "itinerariesLatest",
  updateTripPlan: "updateTripPlan",
  getTripPlanForNDay: "planTripFormAi",
  // chat related urls
  chatInteraction: "itineraries/search",

  // prompt related urls
  getPrompt: `itineraries/getPromptbykey`,
  dayPlanningPrompt: "dayplanningprompt",

  //config
  config: "config",
  configFilter: "config/search",

  // fetch countries
  county: "county",
  flightModify: "trips/flights/change",
  flightCancel: "trips/flights/cancel",
  // "NewMyTripsCancel & Modify" — new flight cancel/modify endpoints (toggled via
  // src/constant/featureSwitches.ts). Same /app/v1/ base as the legacy ones above.
  flightCancelRequest: "flight/cancel/request",
  flightModifySelectSectors: "flight/modify/select-sectors",

  // Post booking hotel api
  shareViaEmail: "shareViaEmail",
  hotelSummary: "trips/hotels",
  postBookingHotelRooms: "trips/hotels/change",
  hotelRebook: "trips/hotels/rebook",
  hotelConfirmRebook: "trips/hotels/rebook/confirm",
  cancelHotels: "trips/hotels/cancel",
  hotelRefundAmount: "trips/hotels/refund",

  // cloudfare verification api
  captchaVerification: "auth/verify-captcha",

  itineraryPrice: "itinerary/pricing",

  // hotel reviews
  hotelReviews: "search/hotels/hotelId/offset/count",

  //airlines preferences
  getAirlinePreference: `data/airlines`,

  //hotel brands
  getHotelBrands: `data/hotel/brands`,

  //getHelp
  getHelp: "getHelp",

  //Promo code
  promoCode: "promos/apply",
  removePromoCode: "promos/remove",
  getPromoCode: "promos/search",
  changePromoCode: "promos/change",

  //Pixel Event
  facebookPixel: "facebook/pixelEvent",

  //Geolocation
  geolocation: "geolocation",

  // Activities
  activitySearch: "search/activities",
  activityDetail: "search/activities/details",
  activityReviews: "search/activities/reviews",
  activitySchedule: "search/activities/availability/schedule",
  activityAvailability: "search/activities/availability",
  activityAvailabilityConfirm: "search/activities/availability/confirm",
  activityMeetingPickup: "search/activities/locations",
  activityCancelReasons: "search/activities/cancel/reasons",
  activityCancelQuote: "search/activities/cancel/quote",
  activityCancel: "search/activities/cancel",
  activityChange: "search/activities/change",
  activityRebook: "search/activities/rebook",
  activityRebookConfirm: "search/activities/rebook/confirm",
  activityPreferenceTags: "search/activities/preference",
  activityTypeaheadSuggestions: "search/activities/typeahead/suggestions",

  // lares (trip-monitoring alert resolution). All proxied by app-backend's
  // LaresController to the lares App API (SigV4). fetchApi substitutes the
  // :token placeholders from the `params` arg. Locked wire contract: GET routes
  // carry userId (+ itineraryId on the alert routes) as QUERY params; POST routes
  // carry { userId, itineraryId } (+ variantId) in the body. itineraryId is
  // required on every alert route (lares 400s without it). Ownership enforced
  // lares-side via userId === alert.userId.
  // Locked contract (2026-06-01): three POST trip-scoped reads, body { itineraryId }
  // only — app-backend injects userId from the validated JWT (no userId in the query;
  // also sidesteps the SigV4 GET-signing bug). Per-alert writes stay POST with
  // { itineraryId, variantId? } in the body.
  laresTripItinerary: "lares/trip/itinerary",
  laresTripAlerts: "lares/trip/alerts",
  laresTripWeather: "lares/trip/weather",
  laresView: "lares/alert/:alertId/view",
  laresDecision: "lares/alert/:alertId/decision",
  laresReject: "lares/alert/:alertId/reject",
  laresRefund: "lares/alert/:alertId/refund",
};
